import React, { useEffect, useRef, useState } from 'react';
import {
  DimensionCategoryCondition,
  clearSearchResults,
  searchQuery,
} from '../slice';
import { StringMap, StringToBoolMap } from 'interfaces';
import { currentConditionMap } from '../currentConditionOptions';
import { nanoid } from 'nanoid';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import * as Styled from './index.styles';

import TextFieldSearch from 'components/TextFieldSearch/TextFieldSearch';
import _ from 'lodash';
import { CaretDown } from '@phosphor-icons/react';
import { TYPE_AT_LEAST_3_CHARACTERS } from '../../../content';
import { CircularProgress } from '@material-ui/core';

interface AutoCompleteProps {
  condition: DimensionCategoryCondition;
  selectedClasses: any;
  onBlurFunction: any;
  currentConditionValueString: any;
  customOption?: string;
  customLabel?: string;
  onHelperTextVisibilityChange?: (isVisible: boolean) => void;
}

interface CurrentConditionProps {
  [key: string]: any;
}

const usesSearch: StringToBoolMap = {
  upc: true,
  productCategory: true,
};

const ALTERNATIVE_NAMES_MAP: StringMap = {
  productMerchantName: 'Merchant Name',
};

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_PAGE_SIZE = 500;

const AutoCompleteModule = ({
  condition,
  selectedClasses,
  onBlurFunction,
  currentConditionValueString,
  customOption,
  customLabel,
  onHelperTextVisibilityChange,
}: AutoCompleteProps) => {
  const dispatch = useAppDispatch();
  const currentCondition: CurrentConditionProps = useAppSelector(state => {
    if (state.audienceReducer.currentConditions.byId[condition.id]) {
      return state.audienceReducer.currentConditions.byId[condition.id];
    } else {
      return condition;
    }
  });
  const searchResults = useAppSelector(
    state => state.audienceReducer.searchResults
  );
  const [value, setValue] = useState<string>('');
  const [inputValue, setInputValue] = useState<string>('');
  const [options, setOptions] = useState<Array<string>>([]);
  const [allSearchOptions, setAllSearchOptions] = useState<Array<string>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipNextInputDebounceRef = useRef(false);

  const clearDebouncedSearch = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
  };

  const dispatchSearch = (newInputValue: string, immediate = false) => {
    const trimmedInput = newInputValue.trim();
    clearDebouncedSearch();

    if (!customOption && usesSearch[currentCondition.option]) {
      if (trimmedInput.length >= MIN_SEARCH_LENGTH) {
        const performSearch = () => {
          setIsLoading(true);
          dispatch(
            searchQuery({
              category: currentCondition.option,
              query: trimmedInput,
            })
          );
          if (trimmedInput === value) onBlurFunction(trimmedInput);
        };

        if (immediate) {
          performSearch();
        } else {
          searchDebounceRef.current = setTimeout(
            performSearch,
            SEARCH_DEBOUNCE_MS
          );
        }
      } else {
        setAllSearchOptions([]);
        setOptions([]);
        setIsLoading(false);
        dispatch(clearSearchResults());
      }
      return;
    }

    if (newInputValue) {
      setValue(newInputValue);
      if (newInputValue === value) onBlurFunction(newInputValue);
    }
  };

  useEffect(() => {
    if (customOption) {
      setAllSearchOptions([]);
      setOptions(currentConditionMap[customOption]);
    } else {
      if (usesSearch[currentCondition.option]) {
        if (searchResults.length) {
          const mutatedResults = searchResults.slice();
          mutatedResults.unshift('');
          setAllSearchOptions(mutatedResults);
          setOptions(mutatedResults.slice(0, SEARCH_PAGE_SIZE));
          setIsLoading(false); // Stop loading when results come in
        } else {
          setAllSearchOptions([]);
          setOptions([]);
          setIsLoading(false); // Stop loading when no results
        }
      } else {
        setAllSearchOptions([]);
        setOptions(currentConditionMap[currentCondition.option]);
      }
    }
  }, [currentCondition.option, searchResults]); // listens for option change to set the options and whenever the current upc search results change

  useEffect(() => {
    return () => {
      clearDebouncedSearch();
    };
  }, []);
  
  // Notify parent about helper text visibility
  useEffect(() => {
    const hasHelperText =
      !customOption &&
      usesSearch[currentCondition.option] &&
      inputValue.trim().length > 0 &&
      inputValue.trim().length < MIN_SEARCH_LENGTH;
    onHelperTextVisibilityChange?.(hasHelperText);
  }, [
    customOption,
    currentCondition.option,
    inputValue,
    onHelperTextVisibilityChange,
  ]);
  
  const isArr = Array.isArray(currentCondition[currentConditionValueString]);
  const loadMoreSearchResults = (event: React.UIEvent<HTMLUListElement>) => {
    if (!customOption && usesSearch[currentCondition.option]) {
      if (options.length >= allSearchOptions.length) return;

      const listboxNode = event.currentTarget;
      const remainingScroll =
        listboxNode.scrollHeight - listboxNode.scrollTop - listboxNode.clientHeight;
      const SCROLL_THRESHOLD_PX = 50;
      if (remainingScroll <= SCROLL_THRESHOLD_PX) {
        setOptions(prev =>
          allSearchOptions.slice(
            0,
            Math.min(prev.length + SEARCH_PAGE_SIZE, allSearchOptions.length)
          )
        );
      }
    }
  };

  return (
    <div>
      <Styled.SelectAutoCompleteGroup
        value={
          isArr
            ? currentCondition[currentConditionValueString][0]
            : currentCondition[currentConditionValueString]
        }
        className={selectedClasses}
        blurOnSelect
        popupIcon={<CaretDown size={20} />}
        // onchange is what is SELECTED in autocomplete component
        onBlur={() => dispatch(clearSearchResults())}
        onChange={(event, newValue) => {
          setValue(newValue);
          if (newValue) {
            setInputValue(newValue);
            if (!customOption) {
              if (usesSearch[currentCondition.option]) {
                onBlurFunction(newValue);
              } else {
                onBlurFunction(newValue);
              }
            } else if (customOption === 'merchantName') {
              onBlurFunction(newValue);
            }
          } else {
            onBlurFunction(''); // we send two parameters just because we could be on the upc option
          }
        }}
        inputValue={inputValue || ''}
        // on input change is for what's typed inside of autocomplete component
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
          if (skipNextInputDebounceRef.current) {
            skipNextInputDebounceRef.current = false;
            return;
          }
          dispatchSearch(newInputValue);
        }}
        id={nanoid()}
        options={options}
        ListboxProps={{
          onScroll: loadMoreSearchResults,
        }}
        noOptionsText="No items found for your search"
        renderInput={params => (
          <TextFieldSearch
            label={
              _.startCase(
                customLabel ||
                  ALTERNATIVE_NAMES_MAP[currentCondition.option] ||
                  currentCondition.option
              ) || ''
            }
            helperText={
              !customOption &&
              usesSearch[currentCondition.option] &&
              inputValue.trim().length < MIN_SEARCH_LENGTH
                ? TYPE_AT_LEAST_3_CHARACTERS
                : undefined
            }
            onPaste={event => {
              if (!customOption && usesSearch[currentCondition.option]) {
                skipNextInputDebounceRef.current = true;
                setTimeout(() => {
                  const nextValue = (
                    event.target as HTMLInputElement | undefined
                  )?.value;
                  if (typeof nextValue === 'string') {
                    setInputValue(nextValue);
                    dispatchSearch(nextValue, true);
                  }
                }, 0);
              }
            }}
            {...params}
            InputProps={{
              ...params.InputProps,
              endAdornment: isLoading ? (
                <CircularProgress size={20} />
              ) : (
                params.InputProps?.endAdornment
              ),
            }}
            variant='outlined'
          />
        )}
        renderOption={(props, option) => (
          <Styled.SelectAutoCompleteGroupOptions>
            <li {...props} key={option}>
              {option}
            </li>
          </Styled.SelectAutoCompleteGroupOptions>
        )}
      />
    </div>
  );
};

export default AutoCompleteModule;
